import { useState, useCallback, useEffect, useRef } from "react";
import { RefreshCcw, Sparkles, Cherry, Citrus, Gem, Star, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const SYMBOLS = [
  { id: "cherry", label: "Cerise", emoji: "🍒", color: "#C65D47" },
  { id: "lemon", label: "Citron", emoji: "🍋", color: "#FFD700" },
  { id: "grape", label: "Raisin", emoji: "🍇", color: "#8C9D78" },
  { id: "seven", label: "7", emoji: "7️⃣", color: "#1D3557" },
  { id: "diamond", label: "Diamant", emoji: "💎", color: "#4FC3F7" },
  { id: "star", label: "Étoile", emoji: "⭐", color: "#FFD700" },
];

const PAYOUTS = {
  "cherry-cherry-cherry": { label: "Triple Cerise", multiplier: "x5", prize: "50€" },
  "seven-seven-seven": { label: "Triple 7", multiplier: "x10", prize: "JACKPOT" },
  "diamond-diamond-diamond": { label: "Triple Diamant", multiplier: "x8", prize: "80€" },
  "star-star-star": { label: "Triple Étoile", multiplier: "x3", prize: "30€" },
  "grape-grape-grape": { label: "Triple Raisin", multiplier: "x4", prize: "40€" },
  "lemon-lemon-lemon": { label: "Triple Citron", multiplier: "x2", prize: "20€" },
};

const Reel = ({ symbols, isSpinning, finalSymbol, reelIndex, onStop }) => {
  const reelRef = useRef(null);
  const [displaySymbols, setDisplaySymbols] = useState([]);

  useEffect(() => {
    // Build a long strip of random symbols
    const strip = [];
    for (let i = 0; i < 30; i++) {
      strip.push(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]);
    }
    if (finalSymbol) {
      strip[strip.length - 1] = finalSymbol;
    }
    setDisplaySymbols(strip);
  }, [finalSymbol, isSpinning]);

  useEffect(() => {
    if (!isSpinning) return;
    const reel = reelRef.current;
    if (!reel) return;

    reel.style.transition = "none";
    reel.style.transform = "translateY(0)";

    requestAnimationFrame(() => {
      const itemHeight = 72;
      const totalDistance = (displaySymbols.length - 1) * itemHeight;
      const duration = 1.5 + reelIndex * 0.6;

      reel.style.transition = `transform ${duration}s cubic-bezier(0.15, 0.85, 0.2, 1)`;
      reel.style.transform = `translateY(-${totalDistance}px)`;

      setTimeout(() => {
        if (onStop) onStop();
      }, duration * 1000);
    });
  }, [isSpinning, displaySymbols, reelIndex, onStop]);

  return (
    <div className="w-20 h-[72px] overflow-hidden rounded-xl bg-white border-2 border-[#1D3557]/20 relative">
      {/* Gradient overlays */}
      <div className="absolute top-0 left-0 right-0 h-4 bg-gradient-to-b from-white to-transparent z-10" />
      <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-t from-white to-transparent z-10" />

      <div ref={reelRef} className="flex flex-col">
        {displaySymbols.map((sym, i) => (
          <div
            key={i}
            className="w-20 h-[72px] flex items-center justify-center flex-shrink-0"
          >
            <span className="text-4xl select-none">{sym.emoji}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const SlotMachine = () => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [results, setResults] = useState([SYMBOLS[0], SYMBOLS[2], SYMBOLS[4]]);
  const [stoppedReels, setStoppedReels] = useState(0);
  const [payout, setPayout] = useState(null);
  const [spinKey, setSpinKey] = useState(0);
  const [credits, setCredits] = useState(10);

  const spin = useCallback(() => {
    if (isSpinning || credits <= 0) return;
    setIsSpinning(true);
    setPayout(null);
    setStoppedReels(0);
    setCredits((c) => c - 1);

    // Determine results (slight bias towards wins for demo)
    const newResults = [];
    const shouldWin = Math.random() > 0.6;
    if (shouldWin) {
      const winSymbol = SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)];
      newResults.push(winSymbol, winSymbol, winSymbol);
    } else {
      for (let i = 0; i < 3; i++) {
        newResults.push(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]);
      }
    }
    setResults(newResults);
    setSpinKey((k) => k + 1);
  }, [isSpinning, credits]);

  const handleReelStop = useCallback(() => {
    setStoppedReels((prev) => {
      const newCount = prev + 1;
      if (newCount >= 3) {
        setIsSpinning(false);
        // Check payout
        setTimeout(() => {
          const key = results.map((r) => r.id).join("-");
          if (PAYOUTS[key]) {
            setPayout(PAYOUTS[key]);
            setCredits((c) => c + parseInt(PAYOUTS[key].multiplier.replace("x", "")) );
          }
        }, 200);
      }
      return newCount;
    });
  }, [results]);

  const resetCredits = () => {
    setCredits(10);
    setPayout(null);
  };

  return (
    <div className="text-center" data-testid="slot-machine">
      {/* Credits */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <div className="flex items-center gap-2 px-4 py-2 bg-[#FFD700]/20 rounded-full border border-[#FFD700]/30">
          <Star className="w-4 h-4 text-[#FFD700]" />
          <span className="text-sm font-bold text-[#1D3557]">{credits} crédits</span>
        </div>
      </div>

      {/* Machine body */}
      <div className="inline-block">
        <div className="bg-gradient-to-b from-[#1D3557] to-[#152A45] rounded-3xl p-6 shadow-2xl relative overflow-hidden">
          {/* Decorative top lights */}
          <div className="flex justify-center gap-3 mb-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-3 h-3 rounded-full"
                style={{
                  backgroundColor: isSpinning
                    ? ["#C65D47", "#FFD700", "#8C9D78", "#4FC3F7", "#C65D47"][i]
                    : "#334155",
                  boxShadow: isSpinning
                    ? `0 0 8px ${["#C65D47", "#FFD700", "#8C9D78", "#4FC3F7", "#C65D47"][i]}`
                    : "none",
                  transition: "all 0.3s",
                  animation: isSpinning ? `pulse 0.5s infinite ${i * 0.1}s` : "none",
                }}
              />
            ))}
          </div>

          {/* Title */}
          <div className="text-center mb-4">
            <h3 className="text-[#FFD700] font-bold text-lg tracking-wider" style={{ fontFamily: "Fraunces, serif" }}>
              MOREATI SLOTS
            </h3>
          </div>

          {/* Reels container */}
          <div className="bg-stone-100 rounded-2xl p-4 flex items-center justify-center gap-3">
            {/* Win line */}
            <div className="absolute left-6 right-6 h-[2px] bg-[#C65D47]/50 z-20 rounded-full" />

            {results.map((symbol, i) => (
              <Reel
                key={`${spinKey}-${i}`}
                symbols={SYMBOLS}
                isSpinning={isSpinning}
                finalSymbol={symbol}
                reelIndex={i}
                onStop={handleReelStop}
              />
            ))}
          </div>

          {/* Payout table mini */}
          <div className="grid grid-cols-3 gap-2 mt-4">
            {Object.values(PAYOUTS).slice(0, 3).map((p, i) => (
              <div key={i} className="text-center bg-white/10 rounded-lg py-1 px-2">
                <p className="text-[#FFD700] text-xs font-bold">{p.multiplier}</p>
                <p className="text-white/60 text-[10px]">{p.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Lever area */}
        <div className="flex justify-center mt-4">
          <Button
            onClick={spin}
            disabled={isSpinning || credits <= 0}
            className="bg-[#C65D47] hover:bg-[#A84A38] rounded-full px-10 h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105 disabled:hover:scale-100"
            data-testid="slot-spin-btn"
          >
            {isSpinning ? (
              <>
                <Zap className="w-5 h-5 mr-2 animate-pulse" />
                En cours...
              </>
            ) : credits <= 0 ? (
              "Plus de crédits"
            ) : (
              <>
                <Sparkles className="w-5 h-5 mr-2" />
                SPIN ({credits} crédits)
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Payout result */}
      {payout && (
        <div
          className="mt-5 p-5 rounded-2xl border-2 bg-gradient-to-r from-[#FFD700]/10 to-[#C65D47]/10 border-[#FFD700]/30 animate-in fade-in slide-in-from-bottom-4 duration-500"
          data-testid="slot-result"
        >
          <div className="flex items-center justify-center gap-3">
            <Sparkles className="w-6 h-6 text-[#FFD700]" />
            <p className="font-bold text-xl text-[#1D3557]">
              {payout.label} — <span className="text-[#C65D47]">{payout.prize}</span>
            </p>
          </div>
        </div>
      )}

      {/* Not a win and game over */}
      {!isSpinning && !payout && stoppedReels >= 3 && (
        <div className="mt-5 p-4 rounded-2xl bg-stone-50 border border-stone-200 animate-in fade-in duration-300">
          <p className="text-stone-500 font-medium">Pas de combinaison gagnante. Retentez !</p>
        </div>
      )}

      {credits <= 0 && !isSpinning && (
        <div className="mt-4">
          <Button onClick={resetCredits} variant="outline" className="rounded-full" data-testid="slot-reset-btn">
            <RefreshCcw className="w-4 h-4 mr-2" />
            Recharger les crédits
          </Button>
        </div>
      )}
    </div>
  );
};

export default SlotMachine;
